/**
 * Goal outlook: everything a goal displays, computed from the balance
 * projection (docs/ACTION-SCHEDULING-DESIGN.md, "Goals: a destination, not a
 * fourth object"). A goal is a readout — a target amount pinned to an
 * account by a date. The projection answers the one question: does the
 * account's curve cross the target line before the date? If not, the
 * "add $X/period" nudge is simple algebra on the existing transfer action.
 */

import { projectBalances } from './balanceProjection';
import { generateOccurrences, cadenceLabel } from './generateOccurrences';
import { matchesConditionGroups } from './transactionMatching';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Projection walking is linear in days; cap the walk and read the value at
// the cap for targets further out (conservative — later contributions would
// only help).
const MAX_HORIZON_DAYS = 730;

const toLocalDay = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const dayDiff = (a, b) => Math.round((a - b) / MS_PER_DAY);

const round2 = (n) => Math.round(n * 100) / 100;

// "Fortnightly" → "fortnight", for the "add $25/fortnight" nudge.
const perNoun = (frequency, interval) => {
  const label = cadenceLabel(frequency, interval);
  const nouns = {
    Daily: 'day', Weekly: 'week', Fortnightly: 'fortnight',
    Monthly: 'month', Quarterly: 'quarter', Yearly: 'year',
  };
  return nouns[label] || label.toLowerCase().replace(/^every /, '');
};

// Fortnightly split of the shortfall — the fallback nudge when there's no
// recurring transfer to hang the suggestion on.
const fortnightlySuggestion = (shortBy, daysUntil) => ({
  amount: Math.ceil(shortBy / Math.max(1, Math.floor(daysUntil / 14))),
  per: 'fortnight',
  actionName: null,
});

/**
 * Outlook for a transaction-tracked goal (trackingType 'transactions').
 * Progress is the sum of transactions matching the goal's rule-style
 * condition groups (from trackFrom onward, when set); the projection is
 * pace-based — contributions so far spread over the days they took, run
 * forward to the target date.
 */
const transactionsGoalOutlook = ({ goal, transactions, target, targetDate, now }) => {
  const from = toLocalDay(goal.trackFrom);
  const matched = (transactions || []).filter((t) => {
    if (!matchesConditionGroups(t, goal.conditionGroups)) return false;
    if (from) {
      const d = toLocalDay(t.date);
      if (!d || d < from) return false;
    }
    return true;
  });

  const absolute = goal.countMode === 'absolute';
  const current = round2(
    matched.reduce(
      (sum, t) => sum + (absolute ? Math.abs(Number(t.amount) || 0) : Number(t.amount) || 0),
      0
    )
  );

  const daysUntil = dayDiff(targetDate, now);
  const base = {
    account: null,
    target,
    targetDate,
    daysUntil,
    current,
    matchedCount: matched.length,
    truncated: false,
  };

  if (daysUntil <= 0) {
    return {
      ...base,
      projected: current,
      onTrack: current >= target,
      shortBy: round2(Math.max(0, target - current)),
      suggestion: null,
    };
  }

  // Pace: contributions so far over the days they took (from trackFrom, or
  // the earliest matched transaction), carried forward to the target date.
  let projected = current;
  const dates = matched.map((t) => toLocalDay(t.date)).filter(Boolean);
  const paceStart = from || (dates.length ? new Date(Math.min(...dates)) : null);
  if (paceStart && current > 0) {
    const elapsed = Math.max(1, dayDiff(now, paceStart));
    projected = current + (current / elapsed) * Math.min(daysUntil, MAX_HORIZON_DAYS);
  }
  projected = round2(projected);
  const shortBy = round2(Math.max(0, target - projected));
  const onTrack = shortBy <= 0;

  return {
    ...base,
    projected,
    onTrack,
    shortBy,
    suggestion: onTrack ? null : fortnightlySuggestion(shortBy, daysUntil),
    truncated: daysUntil > MAX_HORIZON_DAYS,
  };
};

/**
 * @returns {{
 *   account: Object|null, target: number, targetDate: Date, daysUntil: number,
 *   current: number, projected: number, onTrack: boolean, shortBy: number,
 *   suggestion: {amount: number, per: string, actionName: string|null}|null,
 *   truncated: boolean, matchedCount?: number,
 * }|null} null when the goal's account doesn't exist or inputs are invalid.
 * `current` is today's progress figure — the account balance for
 * account-pinned goals, the matched-transaction sum for pattern goals.
 */
export const goalOutlook = ({
  goal,
  accounts = [],
  actions = [],
  budgets = [],
  transactions = [],
  today = new Date(),
} = {}) => {
  if (!goal) return null;
  const target = Number(goal.targetAmount);
  const targetDate = toLocalDay(goal.targetDate);
  const now = toLocalDay(today);
  if (!Number.isFinite(target) || !targetDate) return null;

  if (goal.trackingType === 'transactions') {
    return transactionsGoalOutlook({ goal, transactions, target, targetDate, now });
  }

  const account = accounts.find((a) => a.id === goal.accountId && a.isActive !== false);
  if (!account) return null;

  const daysUntil = dayDiff(targetDate, now);
  const balance = Number(account.balance) || 0;

  // Target date reached: the live balance is the verdict.
  if (daysUntil <= 0) {
    return {
      account,
      target,
      targetDate,
      daysUntil,
      current: round2(balance),
      projected: round2(balance),
      onTrack: balance >= target,
      shortBy: round2(Math.max(0, target - balance)),
      suggestion: null,
      truncated: false,
    };
  }

  const days = Math.min(daysUntil, MAX_HORIZON_DAYS);
  const projection = projectBalances({ accounts, actions, budgets, transactions, today: now, days });
  const projected = projection.rows[projection.rows.length - 1][account.id];
  const shortBy = round2(Math.max(0, target - projected));
  const onTrack = shortBy <= 0;

  // The nudge: spread the shortfall across the remaining occurrences of the
  // recurring transfer that already feeds this account; without one, suggest
  // a fortnightly figure (the default cadence of NZ pay).
  let suggestion = null;
  if (!onTrack) {
    const contributor = actions.find(
      (a) =>
        a && a.isActive !== false && !a.isCompleted &&
        a.type === 'transfer' &&
        a.destinationAccountId === account.id &&
        a.frequency && a.frequency !== 'once'
    );
    if (contributor) {
      const remaining = generateOccurrences(contributor, now, targetDate).length;
      if (remaining > 0) {
        suggestion = {
          amount: Math.ceil(shortBy / remaining),
          per: perNoun(contributor.frequency, contributor.interval),
          actionName: contributor.name,
        };
      }
    }
    if (!suggestion) {
      suggestion = fortnightlySuggestion(shortBy, daysUntil);
    }
  }

  return {
    account,
    target,
    targetDate,
    daysUntil,
    current: round2(balance),
    projected: round2(projected),
    onTrack,
    shortBy,
    suggestion,
    truncated: daysUntil > MAX_HORIZON_DAYS,
  };
};
